import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  ExternalLink,
  Youtube,
  Tv,
  Twitter,
  Instagram,
  Globe,
  Gamepad2,
  Link as LinkIcon,
} from "lucide-react";

const PLATFORM_ICONS = {
  youtube: Youtube,
  twitch: Tv,
  twitter: Twitter,
  instagram: Instagram,
  tiktok: Globe,
  kick: Globe,
};

const PLATFORM_COLORS = {
  youtube: "from-red-500/20 to-red-600/10 border-red-500/20 hover:border-red-500/40",
  twitch: "from-purple-500/20 to-purple-600/10 border-purple-500/20 hover:border-purple-500/40",
  twitter: "from-blue-400/20 to-blue-500/10 border-blue-400/20 hover:border-blue-400/40",
  instagram: "from-pink-500/20 to-orange-500/10 border-pink-500/20 hover:border-pink-500/40",
  tiktok: "from-gray-400/20 to-gray-500/10 border-gray-400/20 hover:border-gray-400/40",
  kick: "from-green-500/20 to-green-600/10 border-green-500/20 hover:border-green-500/40",
};

function InfluencerLinksPage() {
  const { slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [campaignLinks, setCampaignLinks] = useState([]);
  const [externalGames, setExternalGames] = useState([]);
  const [customLinks, setCustomLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        // Look up influencer by username first, then fall back to utmId
        let userSnap = await getDocs(
          query(
            collection(db, "users"),
            where("influencerProfile.username", "==", slug)
          )
        );
        if (userSnap.empty) {
          userSnap = await getDocs(
            query(
              collection(db, "users"),
              where("utmId", "==", slug)
            )
          );
        }
        if (userSnap.empty || userSnap.docs[0].data().role !== "influencer") {
          setNotFound(true);
          setLoading(false);
          return;
        }
        const userDocId = userSnap.docs[0].id;
        const userData = userSnap.docs[0].data();
        setProfile(userData);
        setCustomLinks(userData.linkPageLinks || []);
        setExternalGames(userData.externalGames || []);

        // Fetch campaign joins for this influencer
        const q = query(
          collection(db, "campaignJoins"),
          where("influencerId", "==", userDocId)
        );
        const joinsSnap = await getDocs(q);
        const joins = joinsSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        // Get hidden campaign IDs
        const hiddenCampaigns = userData.linkPageHiddenCampaigns || [];

        // Fetch campaign details for cover images and status
        const visibleJoins = joins.filter(
          (j) => !hiddenCampaigns.includes(j.campaignId)
        );

        const campaignData = [];
        for (const join of visibleJoins) {
          try {
            const campSnap = await getDoc(doc(db, "campaigns", join.campaignId));
            if (campSnap.exists()) {
              const camp = campSnap.data();
              if (camp.status === "active") {
                campaignData.push({
                  ...join,
                  campaignCoverImageUrl: camp.coverImageUrl || join.campaignCoverImageUrl || "",
                  campaignTitle: camp.gameName || camp.title || join.campaignTitle || "Campaign",
                  campaignDescription: camp.description || "",
                });
              }
            }
          } catch {
            // skip failed fetches
          }
        }
        setCampaignLinks(campaignData);
      } catch (err) {
        console.error("Failed to fetch link page data:", err);
        setNotFound(true);
      }
      setLoading(false);
    }
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center text-white">
        <div className="text-center">
          <Globe className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Page Not Found</h1>
          <p className="text-gray-500 text-sm">This link page doesn't exist.</p>
        </div>
      </div>
    );
  }

  const socialLinks = profile?.influencerProfile?.links || {};
  const platforms = profile?.influencerProfile?.platforms || [];

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Background gradient */}
      <div className="fixed inset-0 bg-gradient-to-b from-brand-600/10 via-gray-950 to-gray-950 pointer-events-none" />

      <div className="relative max-w-lg mx-auto px-4 py-10">
        {/* Profile header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-brand-500 to-purple-600 flex items-center justify-center mx-auto mb-4 text-2xl font-bold shadow-lg shadow-brand-500/20">
            {(profile.influencerProfile?.username || profile.displayName || "?")[0].toUpperCase()}
          </div>
          <h1 className="text-2xl font-bold mb-1">{profile.influencerProfile?.username || profile.displayName}</h1>
          {profile.influencerProfile?.audienceSize && (
            <p className="text-sm text-gray-500">Content Creator</p>
          )}
        </div>

        {/* Social platform links */}
        {platforms.length > 0 && (
          <div className="flex justify-center gap-3 mb-8">
            {platforms.map((platformId) => {
              const url = socialLinks[platformId];
              if (!url) return null;
              const Icon = PLATFORM_ICONS[platformId] || Globe;
              return (
                <a
                  key={platformId}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-xl bg-gray-800/80 border border-gray-700/50 hover:border-gray-600 flex items-center justify-center text-gray-400 hover:text-white transition-all hover:scale-105"
                >
                  <Icon className="w-5 h-5" />
                </a>
              );
            })}
          </div>
        )}

        {/* Campaign links + External games */}
        {(campaignLinks.length > 0 || externalGames.length > 0) && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Gamepad2 className="w-4 h-4 text-brand-400" />
              <h2 className="text-sm font-medium text-gray-400">Games that are referred in my account</h2>
            </div>
            <div className="space-y-3">
              {campaignLinks.map((link) => (
                <a
                  key={link.id}
                  href={link.utmLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 bg-gray-900/60 border border-gray-800/60 hover:border-brand-500/30 rounded-2xl p-4 transition-all hover:scale-[1.01] group"
                >
                  {link.campaignCoverImageUrl ? (
                    <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0">
                      <img
                        src={link.campaignCoverImageUrl}
                        alt={link.campaignTitle}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-14 h-14 rounded-xl bg-gray-800 flex items-center justify-center flex-shrink-0">
                      <Gamepad2 className="w-6 h-6 text-gray-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm group-hover:text-brand-400 transition-colors truncate">
                      {link.campaignTitle}
                    </p>
                    {link.campaignDescription && (
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {link.campaignDescription}
                      </p>
                    )}
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-600 group-hover:text-brand-400 transition-colors flex-shrink-0" />
                </a>
              ))}
              {externalGames.map((game, i) => {
                const gameUrl = (() => {
                  try {
                    const u = new URL(game.url);
                    if (u.hostname.includes("steampowered.com") || u.hostname.includes("store.steampowered.com")) {
                      u.searchParams.set("utm_source", "referred.games");
                    }
                    return u.toString();
                  } catch {
                    return game.url;
                  }
                })();
                return (
                <a
                  key={`ext-${i}`}
                  href={gameUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 bg-gray-900/60 border border-gray-800/60 hover:border-green-500/30 rounded-2xl p-4 transition-all hover:scale-[1.01] group"
                >
                  <div className="w-14 h-14 rounded-xl bg-gray-800 flex items-center justify-center flex-shrink-0">
                    <Gamepad2 className="w-6 h-6 text-green-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm group-hover:text-green-400 transition-colors truncate">
                      {game.name}
                    </p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-600 group-hover:text-green-400 transition-colors flex-shrink-0" />
                </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Custom links */}
        {customLinks.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <LinkIcon className="w-4 h-4 text-gray-400" />
              <h2 className="text-sm font-medium text-gray-400">Links</h2>
            </div>
            <div className="space-y-3">
              {customLinks.map((link, i) => {
                const colorClass =
                  PLATFORM_COLORS[link.icon] ||
                  "from-gray-500/20 to-gray-600/10 border-gray-700/50 hover:border-gray-600/50";
                const Icon = PLATFORM_ICONS[link.icon] || Globe;
                return (
                  <a
                    key={i}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-4 bg-gradient-to-r ${colorClass} border rounded-2xl p-4 transition-all hover:scale-[1.01] group`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-gray-800/50 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-gray-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">
                        {link.title || link.url}
                      </p>
                      {link.title && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          {link.url}
                        </p>
                      )}
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-600 group-hover:text-white transition-colors flex-shrink-0" />
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center pt-6 pb-4">
          <p className="text-xs text-gray-700">
            Powered by <a href="https://referred.games" target="_blank" rel="noopener noreferrer" className="text-gray-500 font-medium hover:text-white transition-colors">referred.games</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default InfluencerLinksPage;
